import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Sparkles, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { scrollToId } from "../lib/lenis";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled || open ? "glass shadow-[0_8px_30px_-15px_rgba(27,41,33,0.25)] py-3" : "py-5"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between">
        <button onClick={() => go("home")} aria-label="Biovital home" data-testid="header-logo">
          <Logo light={!scrolled && !open} />
        </button>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className={`group relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                scrolled ? "text-ink hover:text-sage" : "text-cream/90 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="header-phone-link"
            className={`hidden md:inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
              scrolled ? "text-sage hover:text-gold" : "text-cream hover:text-gold-light"
            }`}
          >
            <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
          </a>
          <a href={PHONE_TEL} className="btn-gold !px-4 !py-2.5 whitespace-nowrap md:!px-5" data-testid="header-book-btn">
            <Phone className="h-4 w-4 md:hidden" /> <span className="hidden sm:inline">Prenota Ora</span><span className="sm:hidden">Chiama</span>
          </a>
          <button
            onClick={() => setOpen(!open)}
            className={`lg:hidden rounded-full p-2 ${scrolled || open ? "text-ink" : "text-cream"}`}
            aria-label="Apri menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="container-x flex flex-col gap-1 pt-6 pb-8">
              {NAV_LINKS.map((l, i) =>
                l.id === "novita" ? (
                  <motion.button
                    key={l.id}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    onClick={() => go(l.id)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="my-3 flex items-center gap-4 rounded-2xl bg-sage px-5 py-4 text-left text-cream shadow-[0_16px_30px_-18px_rgba(107,93,75,0.7)]"
                  >
                    <Sparkles className="h-5 w-5 shrink-0 text-cream/90" />
                    <span className="flex-1 text-2xl font-medium leading-tight">{l.label}</span>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-cream/80" />
                  </motion.button>
                ) : (
                  <motion.button
                    key={l.id}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    onClick={() => go(l.id)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="text-left font-serif text-3xl text-ink py-2 border-b border-sage/10"
                  >
                    {l.label}
                  </motion.button>
                )
              )}
              <a href={PHONE_TEL} className="mt-5 inline-flex items-center gap-2 text-sage font-semibold" data-testid="mobile-phone-link">
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
